import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import TextMessage from "../text-message/text-message";
import QuickReplies, { ButtonIcon } from "../quick-replies";
import { buttonClickHandler } from "../buttons/button-click-handler";
import { accentStyle } from "../../../../helpers/utils/stylecss-utils";
import "./index.css";

export const DEFAULT_PAGE_SIZE = 4;
const MAX_PAGE_SIZE = 20;
// A subtitle this short reads as a value ("390 kcal", "$24") and sits beside
// the title; a longer one is a line of its own under it.
const META_MAX_CHARS = 22;
const DESCRIPTION_CLAMP_CHARS = 140;

/**
 * CARDS: { cards: [{ title, subtitle?, meta?, description?, image?, buttons? }],
 *          displayText?, pageSize?, fallbackConfig? }
 *
 * Rendered as a list - thumbnail, title with a short value on the right, the
 * description, then the card's actions - paged on the client so a long result
 * stays one message tall.
 */
export function pageSizeOf(message) {
  const size = Number(message?.pageSize);
  return Number.isFinite(size) && size >= 1 ? Math.min(Math.floor(size), MAX_PAGE_SIZE) : DEFAULT_PAGE_SIZE;
}

function CardThumb({ image, title }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [image]);
  if (!image || failed) {
    return (
      <span className="chat-card-thumb is-empty" aria-hidden="true">
        {String(title || "?").trim().charAt(0).toUpperCase() || "?"}
      </span>
    );
  }
  return <img className="chat-card-thumb" src={image} alt="" loading="lazy" decoding="async" onError={() => setFailed(true)} />;
}

function CardDescription({ text }) {
  const [expanded, setExpanded] = useState(false);
  const long = text.length > DESCRIPTION_CLAMP_CHARS;
  return (
    <div className="chat-card-description">
      <ReactMarkdown className={long && !expanded ? "is-clamped" : undefined}>{text}</ReactMarkdown>
      {long && (
        <button type="button" className="chat-card-more" aria-expanded={expanded} onClick={() => setExpanded(value => !value)}>
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

function Card({ item, onAction, disabled }) {
  const subtitle = typeof item.subtitle === "string" ? item.subtitle.trim() : "";
  const meta = item.meta || (subtitle && subtitle.length <= META_MAX_CHARS ? subtitle : "");
  const subline = meta === subtitle ? "" : subtitle;
  const buttons = (Array.isArray(item.buttons) ? item.buttons : []).filter(button => button && button.title);
  const description = typeof item.description === "string" ? item.description.trim() : "";

  return (
    <li className="chat-card">
      <CardThumb image={item.image} title={item.title} />
      <div className="chat-card-body">
        <div className="chat-card-head">
          <h3 className="chat-card-title">{item.title}</h3>
          {meta && <span className="chat-card-meta">{meta}</span>}
        </div>
        {subline && <p className="chat-card-subtitle">{subline}</p>}
        {description && <CardDescription text={description} />}
        {buttons.length > 0 && (
          <div className="chat-card-actions">
            {buttons.map((button, index) => (
              <button
                key={button.id || index}
                type="button"
                className={`chat-card-action${index === 0 ? " is-primary" : ""}`}
                disabled={disabled}
                onClick={() => onAction(button)}
              >
                <ButtonIcon icon={button.icon} />
                <span className="chat-card-action-label">{button.title}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </li>
  );
}

export default function CardList(props) {
  const { message = {}, onInvokeAction, chatBotSetting, disabled, senderType } = props;
  const { displayText, fallbackConfig } = message || {};
  const cards = Array.isArray(message?.cards) ? message.cards.filter(Boolean) : [];
  const pageSize = pageSizeOf(message);
  const pageCount = Math.max(1, Math.ceil(cards.length / pageSize));

  const [page, setPage] = useState(0);
  const listRef = useRef(null);
  const turned = useRef(false);
  useEffect(() => {
    turned.current = false;
    setPage(0);
  }, [message]);
  // Keep the list's top in view when a page turn changes its height.
  useEffect(() => {
    if (!turned.current) return;
    turned.current = false;
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    listRef.current?.scrollIntoView?.({ block: "nearest", behavior: reducedMotion ? "auto" : "smooth" });
  }, [page]);

  const current = Math.min(page, pageCount - 1);
  const first = current * pageSize;
  const visible = cards.slice(first, first + pageSize);
  const turn = next => {
    turned.current = true;
    setPage(Math.max(0, Math.min(pageCount - 1, next)));
  };

  const onAction = button => buttonClickHandler(onInvokeAction)(button.payload, button);
  const fallbackButtons = (fallbackConfig?.fallbackActions || []).map(action => ({
    title: action?.title,
    payload: action?.payload,
    actionType: action?.actionType,
    icon: action?.icon,
    slot: action?.slot,
    id: action?.id,
    url: action?.url,
    inputValue: action?.inputValue,
  }));

  return (
    <div className="chat-cards" style={accentStyle(chatBotSetting)}>
      {displayText && <TextMessage message={displayText} senderType={senderType} />}

      {cards.length > 0 ? (
        <div className="chat-card-panel" ref={listRef}>
          <ul className="chat-card-list" aria-label={displayText ? undefined : "Results"}>
            {visible.map((item, index) => (
              <Card key={first + index} item={item} onAction={onAction} disabled={disabled} />
            ))}
          </ul>
          {pageCount > 1 && (
            <div className="chat-card-pager">
              <span aria-live="polite">
                {first + 1}–{first + visible.length} of {cards.length}
              </span>
              <div className="chat-card-pager-dots" aria-hidden="true">
                {pageCount <= 8 && Array.from({ length: pageCount }, (_, index) => (
                  <span key={index} className={index === current ? "is-active" : undefined} />
                ))}
              </div>
              <div className="chat-card-pager-buttons">
                <button type="button" aria-label="Previous page" disabled={current === 0} onClick={() => turn(current - 1)}>
                  <BsChevronLeft />
                </button>
                <button type="button" aria-label="Next page" disabled={current >= pageCount - 1} onClick={() => turn(current + 1)}>
                  <BsChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <p className="chat-card-empty">No results found.</p>
      )}

      {fallbackConfig?.fallbackDescription && (
        <div className="mt-3">
          <TextMessage message={fallbackConfig.fallbackDescription} senderType={senderType} />
        </div>
      )}
      <QuickReplies buttons={fallbackButtons} chatBotSetting={chatBotSetting} disabled={disabled} onSelect={onAction} />
    </div>
  );
}
